import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

const BackgroundPaws = () => {
  const [paws, setPaws] = useState([]);
  const pawsCreated = useRef(false);
  const resizeTimeoutRef = useRef(null);

  // Define paw emojis to use
  const pawEmojis = ['🐾', '🐶', '🐱', '🐰', '🐹', '🐦'];

  // Calculate optimal number of paws based on screen size
  useEffect(() => {
    const calculatePawCount = () => {
      const width = window.innerWidth;
      // Significantly increased number of paws for better visibility
      return Math.max(20, Math.min(40, Math.floor(width / 40)));
    };

    const createPaws = () => {
      // Only create paws if they haven't been created yet or during a legitimate resize
      if (!pawsCreated.current) {
        const pawsCount = calculatePawCount();
        
        const newPaws = Array.from({ length: pawsCount }, (_, i) => ({
          id: `paw-${i}-${Date.now()}`, // Unique ID to prevent re-renders
          emoji: pawEmojis[Math.floor(Math.random() * pawEmojis.length)],
          left: Math.random() * 100,
          top: Math.random() * 100,
          size: Math.random() * 30 + 20, // Even larger paws (20-50px)
          opacity: Math.random() * 0.4 + 0.5, // Higher opacity (0.5-0.9)
          animationDelay: Math.random() * 5,
          animationDuration: Math.random() * 5 + 10, // Animation duration
          zIndex: 0, // Changed from -1 to 0 to ensure visibility
          rotation: Math.random() * 360 // Random rotation
        }));

        setPaws(newPaws);
        pawsCreated.current = true;
      }
    };

    // Create paws initially
    createPaws();

    // Handle resize with debounce to prevent unnecessary re-renders
    const handleResize = () => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      
      // Only recreate paws on significant resize events (with debounce)
      resizeTimeoutRef.current = setTimeout(() => {
        const oldWidth = window.innerWidth;
        const oldPawCount = calculatePawCount();
        
        // Check if we need to adjust the number of paws significantly
        const newWidth = window.innerWidth;
        const newPawCount = Math.max(20, Math.min(40, Math.floor(newWidth / 40)));
        
        // Only recreate paws if there's a significant change in viewport size
        // This prevents recreation during small viewport changes like mobile scrolling
        if (Math.abs(oldWidth - newWidth) > 100 || Math.abs(oldPawCount - newPawCount) > 5) {
          pawsCreated.current = false; // Allow recreation
          createPaws();
        }
      }, 300); // Debounce for 300ms
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="paws-background fixed inset-0 pointer-events-none overflow-hidden will-change-transform">
      {paws.map((paw) => (
        <motion.div
          key={paw.id}
          className="paw-bg absolute"
          initial={{ 
            opacity: 0,
            scale: 0.5,
            rotate: paw.rotation
          }}
          animate={{ 
            opacity: paw.opacity,
            scale: 1,
            y: [0, -20, 0, -12, 0], // Increased movement range
            x: [0, 12, 0, -12, 0],   // Increased movement range
            rotate: [paw.rotation, paw.rotation + 10, paw.rotation - 10, paw.rotation] // Subtle rotation
          }}
          transition={{ 
            y: { 
              repeat: Infinity, 
              duration: paw.animationDuration,
              ease: "easeInOut" 
            },
            x: {
              repeat: Infinity,
              duration: paw.animationDuration * 0.7,
              ease: "easeInOut"
            },
            rotate: {
              repeat: Infinity,
              duration: paw.animationDuration * 1.5,
              ease: "easeInOut"
            },
            opacity: { duration: 1.5 },
            scale: { duration: 1 }
          }}
          style={{
            left: `${paw.left}%`,
            top: `${paw.top}%`,
            fontSize: `${paw.size}px`,
            filter: "drop-shadow(0 0 5px rgba(150, 75, 0, 0.3))", // Brown-ish glow effect for paws
            zIndex: paw.zIndex,
            willChange: 'transform', // Optimize for animation performance
            transform: 'translateZ(0)' // Force GPU acceleration
          }}
        >
          {paw.emoji}
        </motion.div>
      ))}
    </div>
  );
};

export default BackgroundPaws;